type EdgeFunctionErrorResponse = {
  error: {
    code: string
    message: string
  }
}

export type EdgeFunctionError<Code extends string> = {
  code: Code
  message: string
}

export type EdgeFunctionResult<Data, Code extends string> =
  | { success: true; data: Data }
  | { success: false; error: EdgeFunctionError<Code> }

type EdgeFunctionErrorContext = {
  status: number
  json: () => Promise<unknown>
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isEdgeFunctionErrorContext = (value: unknown): value is EdgeFunctionErrorContext => {
  return isRecord(value) && typeof value.status === 'number' && typeof value.json === 'function'
}

const isClientErrorStatus = (status: number): boolean => {
  return status >= 400 && status < 500
}

const isEdgeFunctionErrorResponse = (value: unknown): value is EdgeFunctionErrorResponse => {
  if (!isRecord(value)) return false

  const { error } = value
  return isRecord(error) && typeof error.code === 'string' && typeof error.message === 'string'
}

export const parseEdgeFunctionErrorResponse = async (error: unknown): Promise<EdgeFunctionErrorResponse | null> => {
  if (!isRecord(error)) return null

  const { context } = error
  if (!isEdgeFunctionErrorContext(context) || !isClientErrorStatus(context.status)) return null

  try {
    const body = await context.json()
    if (!isEdgeFunctionErrorResponse(body)) return null

    return body
  } catch {
    return null
  }
}

export const toEdgeFunctionFailure = async <Code extends string>(
  error: unknown,
  allowedCodes: readonly Code[]
): Promise<{ success: false; error: EdgeFunctionError<Code> }> => {
  const response = await parseEdgeFunctionErrorResponse(error)
  if (response) {
    const matchedCode = allowedCodes.find((code) => code === response.error.code)
    if (matchedCode) {
      return {
        success: false,
        error: {
          code: matchedCode,
          message: response.error.message,
        },
      }
    }
  }

  throw error
}
